<form [formGroup]="siteForm" (ngSubmit)="onSubmit()" class="space-y-4">
  <div *ngIf="showUrl && !isEdit">
    <label class="block text-sm font-medium mb-1">Site Domain</label>
    @if (availableDomains.length === 0) {
      <div class="border rounded px-3 py-2 bg-yellow-50 text-sm text-yellow-800">
        No issued domains available. <a routerLink="/domains" class="text-blue-600 hover:underline">Add a domain</a> and wait for its certificate to be issued before creating a site.
      </div>
    } @else {
      <select
        formControlName="domainId"
        class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        [class.border-red-500]="siteForm.get('domainId')?.invalid">
        <option value="">Select a domain…</option>
        @for (d of availableDomains; track d._id) {
          <option [value]="d._id">{{ d.domain }}</option>
        }
      </select>
      <div *ngIf="siteForm.get('domainId')?.invalid && siteForm.get('domainId')?.touched" class="text-red-500 text-sm mt-1">
        Select a domain
      </div>
    }
  </div>
  <div *ngIf="showUrl && isEdit">
    <label class="block text-sm font-medium mb-1">Site URL</label>
    <div class="w-full border rounded px-3 py-2 bg-gray-50 text-gray-700">{{ siteUrl || siteForm.get('url')?.value }}</div>
  </div>
  
  <h3 class="text-lg font-semibold mt-6 mb-4">Company Information</h3>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <label class="block text-sm font-medium mb-1">Name <span class="text-red-500">*</span></label>
      <input type="text" formControlName="name" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('name')?.invalid">
      <div *ngIf="siteForm.get('name')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('name')?.errors?.['required']">Name is required</div>
      </div>
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">Registered Name <span class="text-red-500">*</span></label>
      <input type="text" formControlName="registeredName" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('registeredName')?.invalid">
      <div *ngIf="siteForm.get('registeredName')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('registeredName')?.errors?.['required']">Registered name is required</div>
      </div>
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">Registered Number</label>
      <input type="text" formControlName="registered_number" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">Google Tag Manager ID</label>
      <input type="text" formControlName="gtm" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">Logo <span class="text-red-500">*</span></label>
      <div class="flex flex-col items-center">
        <div *ngIf="siteForm.get('logo')?.value" class="h-10 flex items-center justify-center mb-4 px-4 py-6">
          <div *ngIf="isSvgMarkup(siteForm.get('logo')?.value)" [innerHTML]="sanitizeSvg(siteForm.get('logo')?.value)" class="h-10 [&>svg]:h-full [&>svg]:w-auto"></div>
          <img *ngIf="!isSvgMarkup(siteForm.get('logo')?.value)" [src]="siteForm.get('logo')?.value" alt="Logo preview" class="h-10 object-contain">
        </div>
        <div class="flex space-x-2">
          <!-- Raster image upload (PNG, JPG etc.) — kept for reference
          <button type="button" (click)="uploadLogo()"
            class="px-3 py-2 bg-white text-gray-700 rounded border border-gray-300 text-sm hover:bg-gray-50 flex items-center space-x-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
            <span>{{ siteForm.get('logo')?.value ? 'Change Logo' : 'Upload Logo' }}</span>
          </button>
          -->
          <button type="button" (click)="svgInput.click()"
            class="px-3 py-2 bg-white text-gray-700 rounded border border-gray-300 text-sm hover:bg-gray-50 flex items-center space-x-1">
            <span>{{ siteForm.get('logo')?.value ? 'Change Logo' : 'Upload Logo' }}</span>
          </button>
          <input #svgInput type="file" accept=".svg" class="hidden" (change)="onSvgFileSelected($event)">
        </div>
        <div *ngIf="siteForm.get('logo')?.invalid && siteForm.get('logo')?.touched" class="text-red-500 text-sm mt-2">Logo is required</div>
        <p class="text-gray-500 text-xs mt-2">Must be SVG format.</p>
      </div>
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">Favicon <span class="text-red-500">*</span></label>
      <div class="flex flex-col items-center">
        <img *ngIf="siteForm.get('favicon.s32')?.value" [src]="siteForm.get('favicon.s180')?.value" alt="Favicon preview" class="h-10 w-10 object-contain mb-4">
        <button type="button" (click)="uploadFavicon()"
          class="px-3 py-2 bg-white text-gray-700 rounded border border-gray-300 text-sm hover:bg-gray-50 flex items-center space-x-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
          <span>{{ siteForm.get('favicon.s32')?.value ? 'Change Favicon' : 'Upload Favicon' }}</span>
        </button>
        <div *ngIf="siteForm.get('favicon.s32')?.invalid && siteForm.get('favicon.s32')?.touched" class="text-red-500 text-sm mt-2">Favicon is required</div>
        <p class="text-gray-500 text-xs mt-2">Upload a square image. Will be resized to 16x16, 32x32, 180x180, 192x192, 512x512.</p>
      </div>
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">Social Image <span class="text-red-500">*</span></label>
      <div class="flex flex-col items-center">
        <img *ngIf="siteForm.get('socialImage')?.value" [src]="siteForm.get('socialImage')?.value" alt="Social image preview" class="h-20 w-20 object-cover rounded mb-4">
        <button type="button" (click)="uploadSocialImage()"
          class="px-3 py-2 bg-white text-gray-700 rounded border border-gray-300 text-sm hover:bg-gray-50 flex items-center space-x-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
          <span>{{ siteForm.get('socialImage')?.value ? 'Change Social Image' : 'Upload Social Image' }}</span>
        </button>
        <div *ngIf="siteForm.get('socialImage')?.invalid && siteForm.get('socialImage')?.touched" class="text-red-500 text-sm mt-2">Social image is required</div>
        <p class="text-gray-500 text-xs mt-2">Used for social media sharing (og:image). Will be cropped square and resized to 1000x1000.</p>
      </div>
    </div>
  </div>
  
  <h3 class="text-lg font-semibold py-4">Contact Information</h3>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <label class="block text-sm font-medium mb-1">Email <span class="text-red-500">*</span></label>
      <input type="email" formControlName="email" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('email')?.invalid">
      <div *ngIf="siteForm.get('email')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('email')?.errors?.['required']">Email is required</div>
        <div *ngIf="siteForm.get('email')?.errors?.['email']">Please enter a valid email</div>
      </div>
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">Phone <span class="text-red-500">*</span></label>
      <input type="tel" formControlName="phone" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('phone')?.invalid">
      <div *ngIf="siteForm.get('phone')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('phone')?.errors?.['required']">Phone is required</div>
      </div>
    </div>
  </div>
  
  <h3 class="text-lg font-semibold py-4">Address</h3>
  <div formGroupName="contact" class="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <label class="block text-sm font-medium mb-1">Address Line 1 <span class="text-red-500">*</span></label>
      <input type="text" formControlName="line1" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('contact.line1')?.invalid">
      <div *ngIf="siteForm.get('contact.line1')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('contact.line1')?.errors?.['required']">Address line 1 is required</div>
      </div>
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">Address Line 2</label>
      <input type="text" formControlName="line2" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">City <span class="text-red-500">*</span></label>
      <input type="text" formControlName="city" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('contact.city')?.invalid">
      <div *ngIf="siteForm.get('contact.city')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('contact.city')?.errors?.['required']">City is required</div>
      </div>
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">County</label>
      <input type="text" formControlName="county" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">Postcode <span class="text-red-500">*</span></label>
      <input type="text" formControlName="postcode" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('contact.postcode')?.invalid">
      <div *ngIf="siteForm.get('contact.postcode')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('contact.postcode')?.errors?.['required']">Postcode is required</div>
      </div>
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">Country</label>
      <input type="text" formControlName="country" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('contact.country')?.invalid">
      <div *ngIf="siteForm.get('contact.country')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('contact.country')?.errors?.['required']">Country is required</div>
      </div>
    </div>
  </div>
  
  <h3 class="text-lg font-semibold py-4">Social Media</h3>
  <div formGroupName="social" class="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <label class="block text-sm font-medium mb-1">Facebook</label>
      <input type="url" formControlName="facebook" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('social.facebook')?.invalid" placeholder="https://facebook.com/yourpage">
      <div *ngIf="siteForm.get('social.facebook')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('social.facebook')?.errors?.['pattern']">Please enter a valid URL</div>
      </div>
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">Instagram</label>
      <input type="url" formControlName="instagram" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('social.instagram')?.invalid" placeholder="https://instagram.com/yourpage">
      <div *ngIf="siteForm.get('social.instagram')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('social.instagram')?.errors?.['pattern']">Please enter a valid URL</div>
      </div>
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">LinkedIn</label>
      <input type="url" formControlName="linkedIn" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('social.linkedIn')?.invalid" placeholder="https://linkedin.com/company/yourcompany">
      <div *ngIf="siteForm.get('social.linkedIn')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('social.linkedIn')?.errors?.['pattern']">Please enter a valid URL</div>
      </div>
    </div>
    <div>
      <label class="block text-sm font-medium mb-1">X</label>
      <input type="url" formControlName="twitter" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('social.twitter')?.invalid" placeholder="https://x.com/yourcompany">
      <div *ngIf="siteForm.get('social.twitter')?.invalid" class="text-red-500 text-sm mt-1">
        <div *ngIf="siteForm.get('social.twitter')?.errors?.['pattern']">Please enter a valid URL</div>
      </div>
    </div>
  </div>

   <h3 class="text-lg font-semibold py-4">Menu Settings</h3>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <label class="text-sm font-medium">Show portal login?</label>
      <label class="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" formControlName="portal" class="sr-only peer">
        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
  
  <h3 class="text-lg font-semibold py-4">Banner Settings</h3>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <label class="text-sm font-medium">White banner title?</label>
      <label class="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" formControlName="bannerTitleWhite" class="sr-only peer">
        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
  </div>

  <h3 class="text-lg font-semibold py-4">Profile Settings</h3>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <label class="text-sm font-medium">Profile bio in modal?</label>
      <label class="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" formControlName="team_modal" class="sr-only peer">
        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
    <div class="flex items-center justify-between">
      <label class="text-sm font-medium">Profile image square?</label>
      <label class="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" formControlName="team_basic" class="sr-only peer">
        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
    </div>
    <h3 class="text-lg font-semibold py-4">Article Settings</h3>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <label class="text-sm font-medium">Include CTA blocks?</label>
      <label class="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" formControlName="blog_cta" class="sr-only peer">
        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
    </div>

   
    <h3 class="text-lg font-semibold py-4">Contact Page</h3>

    <div>
      <label class="block text-sm font-medium mb-1">Text</label>
      <app-tiptap-editor
        formControlName="contact_page">
      </app-tiptap-editor>
    </div>
  </div>
  
    <div class="flex items-center justify-between">
      <label class="text-sm font-medium">Show contact form?</label>
      <label class="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" formControlName="contact_form" class="sr-only peer">
        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
  <h3 class="text-lg font-semibold py-4">Footer</h3>

  <div>
    <label class="block text-sm font-medium mb-1">Text <span class="text-red-500">*</span></label>
    <textarea formControlName="footer_text" rows="3" class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" [class.border-red-500]="siteForm.get('footer_text')?.invalid && siteForm.get('footer_text')?.touched"></textarea>
    <div *ngIf="siteForm.get('footer_text')?.invalid && siteForm.get('footer_text')?.touched" class="text-red-500 text-sm mt-1">Footer text is required</div>
    <p class="text-gray-500 text-xs mt-1" ngNonBindable>Use <code>{{registered_number}}</code> to dynamically insert the registered number.</p>
  </div>
  
  <div class="flex space-x-2 pt-6">
    <button 
      type="submit" 
      [disabled]="siteForm.invalid || isLoading"
      class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center"
    >
      @if (isLoading) {
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      }
      {{ isLoading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Site' : 'Create Site') }}
    </button>
    <button 
      *ngIf="showCancel"
      type="button" 
      (click)="onCancel()"
      class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
    >
      Cancel
    </button>
  </div>
</form>